/* eslint-disable consistent-return */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgressbar } from 'react-circular-progressbar';
import { FaCogs, FaDatabase, FaInfoCircle } from 'react-icons/fa';
import { IoMdPie } from 'react-icons/io';
import { v4 as uuidv4 } from 'uuid';

import { useTheme } from 'styled-components';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultTooltip from '~/components/Tooltips/Default';
import ExitButton from '~/components/Buttons/Exit';
import FileUploader from '~/components/FileUploader';
import SaveButton from '~/components/Buttons/Save';
import Select from '~/components/Select';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container } from './styles';

const DataLoad: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { colors } = useTheme();
  const [quarters, setQuarters] = useState<{ id: string; code: string }[]>([]);
  const [quarterId, setQuarterId] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataLoadInfo, setDataLoadInfo] = useState<{
    load_key: string;
    status: string;
    progress: number;
    error_message: string;
  }>({
    load_key: '',
    status: '',
    progress: 0,
    error_message: '',
  });
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleFileSelect = (file: File | null) => {
    setDataLoadInfo({
      load_key: `${uuidv4()}_${new Date().getTime()}`,
      status: '',
      progress: 0,
      error_message: '',
    });

    setSelectedFile(file);
  };

  useEffect(() => {
    api.get('/quarters/open').then(response => {
      setQuarters(response.data);
    });
  }, []);

  useEffect(() => {
    if (dataLoadInfo.load_key === '') {
      return;
    }

    const interval = setInterval(() => {
      api
        .get(
          `/dataloads/status?` +
            `type=rebate_balances&load_key=${dataLoadInfo.load_key}`,
        )
        .then(response => {
          setDataLoadInfo(prev => ({
            ...prev,
            status: response.data.status,
            progress: response.data.progress,
            error_message: response.data.error_message,
          }));
        });
    }, 3000);

    return () => clearInterval(interval);
  }, [dataLoadInfo.load_key]);

  useEffect(() => {
    if (dataLoadInfo.status === 'finalizado') {
      addToast({
        type: 'success',
        title: 'Carga realizada!',
        description: 'Processo executado com sucesso.',
      });

      history.push('/configurations/rebatebalancequarters');
    } else if (
      dataLoadInfo.status === 'error' &&
      dataLoadInfo.error_message !== ''
    ) {
      setDataLoadInfo(prev => ({
        ...prev,
        load_key: '',
        status: '',
        progress: 0,
      }));

      setIsSaving(false);

      addToast({
        type: 'error',
        title: 'Erro ao realizar carga!',
        description: dataLoadInfo?.error_message,
      });
    }
  }, [addToast, dataLoadInfo, history]);

  const executeDataLoad = () => {
    try {
      if (selectedFile && quarterId && dataLoadInfo.load_key !== '') {
        setIsSaving(true);

        const data = new FormData();

        setDataLoadInfo(prev => ({
          ...prev,
          status: 'processando',
        }));

        data.append('type', 'rebate_balances');
        data.append('load_key', dataLoadInfo.load_key);
        data.append('quarter_id', quarterId);
        data.append('file', selectedFile);

        api.post('/dataloads/rebatebalances', data);
      }
    } catch (error: any) {
      setIsSaving(false);

      addToast({
        type: 'error',
        title: 'Ocorreu um erro!',
        description: error.response.data.message,
      });
    }
  };

  const getQuarterValueForSelect = ():
    | { value: string; label: string }
    | undefined => {
    const findQuarter = quarters.find(q => q.id === quarterId);

    if (findQuarter) {
      return {
        value: findQuarter.id,
        label: findQuarter.code,
      };
    }

    return undefined;
  };

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/rebatebalancequarters"
        firstChild="Saldos Rebate - Trimestres"
        secondChild="Carga de Dados"
        mainIcon={FaCogs}
        firstChildIcon={IoMdPie}
        secondChildIcon={FaDatabase}
        mainColored
        firstChildColored
      />

      <div
        style={{
          display: 'flex',
          marginBottom: 10,
          columnGap: 3,
          fontSize: 14,
          fontStyle: 'italic',
        }}
      >
        <FaInfoCircle size={16} />

        <p>
          Carga de dados para atualização de saldos de rebate para o trimestre
          selecionado
        </p>
      </div>

      <div
        style={{
          marginBottom: 30,
          rowGap: 3,
          fontSize: 12,
          fontStyle: 'italic',
        }}
      >
        <p>Informações importantes sobre o processo:</p>

        <ul
          style={{
            margin: '10px 0px 0px 20px',
            listStyleType: 'disc',
            display: 'flex',
            flexDirection: 'column',
            rowGap: 10,
          }}
        >
          <li>
            Atualiza o saldo e o valor pendente para os grupos informados,
            referente ao trimestre selecionado, porém, não realiza a criação de
            grupos não cadastrados.
          </li>

          <li>
            O valor utilizado para cada grupo não é afetado. Dessa forma, caso
            for informado um novo saldo, inferior ao valor utilizado, ficará um
            valor pendente negativo.
          </li>
        </ul>
      </div>

      <div style={{ margin: '10px 0px 30px 0px', maxWidth: 300 }}>
        <Select
          label="Trimestre"
          options={quarters?.map(q => ({
            value: q.id,
            label: q.code,
          }))}
          value={getQuarterValueForSelect()}
          darkBorder
          onSet={(e: any) => {
            setQuarterId(e.value);
          }}
        />
      </div>

      <div style={{ position: 'relative', width: '100%', maxWidth: 680 }}>
        <FileUploader
          acceptedFileTypes={[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
          ]}
          onFileSelect={handleFileSelect}
        />

        {isSaving && (
          <div
            style={{ position: 'absolute', inset: 0, cursor: 'not-allowed' }}
          />
        )}
      </div>

      {dataLoadInfo?.progress !== undefined && (
        <div
          style={{
            maxWidth: 680,
            marginTop: 40,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CircularProgressbar
            styles={{
              root: {
                width: '150px',
              },
              path: {
                stroke: '#4F46E5',
                strokeLinecap: 'round',
                transition: 'stroke-dashoffset 0.5s ease',
              },
              trail: {
                stroke: '#E5E7EB',
              },
              text: {
                fill: '#1F2937',
                fontSize: '20px',
                fontWeight: 'bold',
                dominantBaseline: 'central',
                textAnchor: 'middle',
              },
            }}
            strokeWidth={10}
            text={`${new Intl.NumberFormat('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(dataLoadInfo?.progress || 0)}%`}
            value={dataLoadInfo?.progress || 0}
          />
        </div>
      )}

      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}
      >
        <DefaultTooltip
          title={
            !quarterId
              ? 'Necessário selecionar trimestre'
              : dataLoadInfo.error_message !== ''
              ? 'Necessário selecionar arquivo novamente'
              : 'Necessário selecionar arquivo'
          }
          background={colors.save}
          borderColor={colors.save}
          disabled={
            quarterId !== undefined &&
            selectedFile !== null &&
            dataLoadInfo.error_message === ''
          }
        >
          <SaveButton
            type="button"
            label="Executar"
            processing={isSaving}
            disabled={
              isSaving ||
              selectedFile === null ||
              dataLoadInfo.error_message !== ''
            }
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />
        </DefaultTooltip>

        <ExitButton
          style={{ marginLeft: 10 }}
          type="button"
          disabled={isSaving}
          onClick={() => setIsOpenExit(true)}
        />
      </div>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => executeDataLoad()}
        title="Confirma execução de carga de dados?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={() => history.push('/configurations/rebatebalancequarters')}
      />
    </Container>
  );
};

export default DataLoad;
